import { useState, useRef, useMemo, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { queryClient } from "@/hooks/use-query-client";
import { BriefcaseMedical, ChevronRight, MapPin } from "lucide-react";
import { useNavigate } from "react-router-dom";

import PageContainer from "./containers/page-container";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import QuickSearch from "./components/provider-search/quick-search";
import type {
  NPIFreeTextType,
  ProviderFreeTextType,
  ProviderSearchCategoryType,
  ProviderSearchSpecialtyType,
  SpecialtyFreeTextType,
} from "@/types";
import {
  useFreeTextSearch,
  useProviderSearchSetting,
} from "@/hooks/use-provider-search";
import {
  DISTANCE_OPTIONS,
  PROVIDER_SETTING_TYPE,
  PROVIDER_SEARCH_FREETEXT_TYPE,
  PROVIDER_SEARCH_TYPES,
  QueryKeys,
} from "@/constants";
import type { RootState } from "@/store/store";
import GroupedSearchAutocomplete from "./components/grouped-search-autocomplete";
import { capitalizeInitialLetter } from "@/lib/string-util";
import type { ProviderSearchSettingType } from "@/model/provider-search";
import {
  resetSearchPayload,
  setCategory,
  setDistance,
  setLocation,
  setProviderSearchDynamicPayload,
  setProviderSearchType,
  setSearchCriteria,
  setSpecialty,
  setSpecialtyList,
  setTaxonomyCode,
  setViewMoreDialog,
} from "@/store/provider-search/provider-search-slice";
import ProviderSearchFreeTextViewMore from "./components/provider-search/freetext-viewmore";
import SettingsIconSkeletonLoader from "./components/provider-search/settings-icon-skeleton-loader";
import { NoDataAvailable, SomethingWentWrong } from "@/pages/components/empty";
import SpecialityListDialog from "./components/provider-search/specialty-list-dialog";
import SearchAutocomplete from "./components/search-autocomplete";
import type Place from "@/model/place";
import { useLocationSearch } from "@/hooks/use-cost-estimator";
import useUserInfo from "@/hooks/use-user-info";
import Dropdown from "./components/drop-down";
import type Distance from "@/model/distance";
import ScreenRouteNames from "@/constants/screen-route-names";
import ValidationDialog from "./components/provider-search/validation-dialog";
import { useUserLocation } from "@/context/location-provider";

const ProviderSearch = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userInfo = useUserInfo();
  const { location: userLocation, isFetching: isLocationFetching } =
    useUserLocation();
  const { category, specialty, distance, location, viewMoreDialog } =
    useSelector((state: RootState) => state.providerSearch);
  const {
    data: settingData,
    isFetching: isSettingFetching,
    isFetched: isSettingFetched,
  } = useProviderSearchSetting();

  const locationSearchQuery = useLocationSearch<Place>(userInfo);

  const searchByCategorySettings = useMemo(() => {
    return settingData?.filter(
      (searchSetting) =>
        searchSetting.type === PROVIDER_SETTING_TYPE.SEARCH_BY_CATEGORY,
    );
  }, [settingData]);

  const [expanded, setExpanded] = useState(false);
  const [openValidationDialog, setOpenValidationDialog] = useState({
    isOpen: false,
    content: "",
  });
  const scrollTargetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT],
    });
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE],
    });
    queryClient.removeQueries({ queryKey: [QueryKeys.PROVIDER_SEARCH] });
    dispatch(resetSearchPayload());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (userLocation?.lat && userLocation.lng) {
      dispatch(
        setLocation({
          description: userLocation.description,
          lat: userLocation.lat,
          lng: userLocation.lng,
          place_id: userLocation.place_id,
          reference: "",
        }),
      );
    }
  }, [userLocation?.lat, userLocation?.lng, dispatch, userLocation]);

  const toggle = () => {
    const isExpanding = !expanded;
    setExpanded(isExpanding);
    if (isExpanding) {
      setTimeout(() => {
        scrollTargetRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      }, 450);
    }
  };

  const onCategorySelect = (selectedCategory: ProviderSearchCategoryType) => {
    if (category && selectedCategory.categoryCode === category?.categoryCode) {
      dispatch(setCategory(null));
      dispatch(
        setSpecialty({
          searchParameter: "",
          specialtyDesc: "",
          type: "",
        }),
      );
      dispatch(setSpecialtyList([]));
    } else {
      dispatch(setTaxonomyCode(""));
      dispatch(setSearchCriteria(""));
      dispatch(setCategory(selectedCategory));
      dispatch(setSpecialtyList(selectedCategory?.specialtyList ?? []));
    }
  };

  const renderSearchByCategorySection = () => {
    if (isSettingFetching || isLocationFetching) {
      return <SettingsIconSkeletonLoader numberOfPlaceholder={20} />;
    } else if (isSettingFetched) {
      if (
        (settingData as ProviderSearchSettingType[])?.length > 0 &&
        searchByCategorySettings
      ) {
        return searchByCategorySettings?.map((searchByCategorySetting, _) => {
          return (
            /* 2.1.1 Keyboard — A / 4.1.2 — A: <Card> renders a plain <div>, so the
               provider-search category tiles were unreachable by keyboard. aria-pressed
               exposes which category is currently selected — previously that state was
               conveyed only by background colour (1.4.1 Use of Color — A). */
            <button
              type="button"
              key={`search-by-category-${searchByCategorySetting.categoryCode}`}
              data-testid="category-card"
              aria-pressed={
                category?.categoryCode === searchByCategorySetting.categoryCode
              }
              className={`flex h-[110px] w-[110px] cursor-pointer items-center justify-center rounded-lg border shadow hover:bg-primary-extraLight focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${category?.categoryCode === searchByCategorySetting.categoryCode ? `border-gray-200 bg-gray-100` : `bg-white`}`}
              onClick={() => onCategorySelect(searchByCategorySetting)}
            >
              <div className="flex flex-col items-center p-2 text-center">
                <img
                  className="mb-4"
                  id={`provider-quick-search-icon-${searchByCategorySetting.categoryCode}`}
                  src={`data:image/svg+xml;base64,${searchByCategorySetting.icon}`}
                  height={32}
                  width={32}
                  alt=""
                />
                <span className="mb-1 text-sm font-medium leading-normal">
                  {searchByCategorySetting?.displayName}
                </span>
              </div>
            </button>
          );
        });
      } else {
        return (
          <NoDataAvailable
            message="There are currently no provider category to display."
            icon={BriefcaseMedical}
          />
        );
      }
    } else {
      return <SomethingWentWrong />;
    }
  };

  const validateSearchParameters = () => {
    const pendingSelections: string[] = [];
    const isSpecialtyInitial =
      specialty.searchParameter?.length === 0 &&
      specialty.specialtyDesc?.length === 0 &&
      specialty.type?.length === 0;
    if (isSpecialtyInitial) pendingSelections.push("Specialty");
    if (category == null) pendingSelections.push("Category");
    if (location == null) pendingSelections.push("Location");
    if (pendingSelections?.length === 0) {
      return { valid: true, message: null };
    }
    const last = pendingSelections.pop();
    const message =
      pendingSelections?.length === 0
        ? `Please select ${last}`
        : `Please select ${pendingSelections.join(", ")} and ${last}`;

    return {
      valid: false,
      message,
    };
  };

  const onSearch = () => {
    const { valid, message } = validateSearchParameters();
    if (valid) {
      dispatch(setTaxonomyCode(""));
      dispatch(setSearchCriteria(""));
      dispatch(
        setProviderSearchType(PROVIDER_SEARCH_TYPES.SEARCH_BY_SPECIALTY),
      );
      dispatch(
        setProviderSearchDynamicPayload({
          category: category?.categoryCode,
          speciality: specialty?.searchParameter,
          specialityDescription: specialty?.specialtyDesc,
        }),
      );
      const targetPath = `/${ScreenRouteNames.PROVIDER_SEARCH_BY_GROUP.path}/${ScreenRouteNames.PROVIDER_SEARCH_RESULT.path}`;
      navigate(targetPath, {
        replace: false,
      });
    } else {
      setOpenValidationDialog({
        isOpen: true,
        content: message as string,
      });
    }
  };

  const onFreeTextResultSelect = (
    selected: SpecialtyFreeTextType | NPIFreeTextType | ProviderFreeTextType,
  ) => {
    if (
      (selected as SpecialtyFreeTextType).type ===
      PROVIDER_SETTING_TYPE.SEARCH_BY_CATEGORY
    ) {
      dispatch(setTaxonomyCode(""));
      dispatch(setSearchCriteria(""));
      dispatch(
        setCategory({
          categoryCode: (selected as SpecialtyFreeTextType).categoryCode,
          categoryDesc: (selected as SpecialtyFreeTextType).categoryDesc,
        }),
      );
      dispatch(
        setSpecialty({
          searchParameter: (selected as SpecialtyFreeTextType).searchParameter,
          specialtyDesc: (selected as SpecialtyFreeTextType).specialtyDesc,
          type: (selected as SpecialtyFreeTextType).type,
        }),
      );
      dispatch(
        setProviderSearchType(PROVIDER_SEARCH_TYPES.SEARCH_BY_SPECIALTY),
      );
      dispatch(
        setProviderSearchDynamicPayload({
          category: (selected as SpecialtyFreeTextType).categoryCode,
          speciality: (selected as SpecialtyFreeTextType).searchParameter,
          specialityDescription: (selected as SpecialtyFreeTextType)
            .specialtyDesc,
        }),
      );
      const targetPath = `/${ScreenRouteNames.PROVIDER_SEARCH_BY_GROUP.path}/${ScreenRouteNames.PROVIDER_SEARCH_RESULT.path}`;
      navigate(targetPath, {
        replace: false,
      });
    } else {
      dispatch(setTaxonomyCode(""));
      dispatch(setCategory(null));
      dispatch(
        setSpecialty({
          searchParameter: "",
          specialtyDesc: "",
          type: "",
        }),
      );
      dispatch(
        setSearchCriteria(
          ((selected as NPIFreeTextType) || (selected as ProviderFreeTextType))
            .npi,
        ),
      );
      dispatch(setProviderSearchType(PROVIDER_SEARCH_TYPES.FREETEXT_SEARCH));
      dispatch(
        setProviderSearchDynamicPayload({
          searchCriteria: (
            (selected as NPIFreeTextType) || (selected as ProviderFreeTextType)
          ).npi,
        }),
      );
      const targetPath = `/${ScreenRouteNames.PROVIDER_SEARCH_BY_GROUP.path}/${ScreenRouteNames.PROVIDER_SEARCH_RESULT.path}`;
      navigate(targetPath, {
        replace: false,
      });
    }
    dispatch(
      setViewMoreDialog({
        isVisible: false,
        searchBy: "",
        viewMoreSearchCriteria: "",
      }),
    );
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT],
    });
    queryClient.removeQueries({
      queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE],
    });
  };

  return (
    <PageContainer title="Provider Search">
      <div className="space-y-6 p-8" ref={scrollTargetRef}>
        {/* Quick Search Section */}
        <section>
          <h2 className="mb-2 text-sm font-medium text-gray-700">
            Quick Search
          </h2>
          <div className="flex flex-wrap gap-4">
            <QuickSearch />
          </div>
        </section>
        {/* Search by Provider, Specialty or Type (Free Text Search) */}
        <section>
          <h2 className="mb-2 text-sm font-medium text-gray-700">
            Search by Provider (Name or NPI), Specialty, or Type
          </h2>
          <GroupedSearchAutocomplete
            placeholder="E.g. X-ray or Hospital"
            queryFunction={(query) => {
              // eslint-disable-next-line react-hooks/rules-of-hooks
              return useFreeTextSearch({ searchCriteria: query }, settingData!);
            }}
            cacheQueryKey={[QueryKeys.PROVIDER_SEARCH_FREE_TEXT]}
            disabled={isSettingFetching || location === null ? true : false}
            formatResult={(sectionKey, item) => {
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.NPI) {
                const i = item as NPIFreeTextType;
                return (
                  <div className="w-full">
                    <div className="flex cursor-pointer items-center justify-between bg-white p-0.5">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-foreground">
                          {" "}
                          {capitalizeInitialLetter(i.name)}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          NPI: {i.npi}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {i.distance < 1
                              ? Math.round(i.distance * 10) / 10
                              : Math.round(i.distance)}{" "}
                            miles{" "}
                          </p>
                          <p className="text-xs text-gray-500">(approx.)</p>
                        </div>
                        <ChevronRight className="size-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              }
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.PROVIDER) {
                const i = item as ProviderFreeTextType;
                return (
                  <div className="w-full">
                    <div className="flex cursor-pointer items-center justify-between bg-white p-0.5">
                      <div className="flex-1">
                        <h3 className="text-sm font-medium text-foreground">
                          {" "}
                          {capitalizeInitialLetter(i.name)}
                        </h3>
                        <p className="text-xs text-muted-foreground">
                          NPI: {i.npi}
                        </p>
                      </div>
                      <div className="flex items-center">
                        <div className="text-right">
                          <p className="text-sm font-medium text-gray-900">
                            {i.distance < 1
                              ? Math.round(i.distance * 10) / 10
                              : Math.round(i.distance)}{" "}
                            miles{" "}
                          </p>
                          <p className="text-xs text-gray-500">(approx.)</p>
                        </div>
                        <ChevronRight className="size-5 text-gray-400" />
                      </div>
                    </div>
                  </div>
                );
              }
              if (sectionKey === PROVIDER_SEARCH_FREETEXT_TYPE.SPECIALITY) {
                const i = item as SpecialtyFreeTextType;
                return (
                  <div>
                    <div className="text-medium font-normal">
                      {i.specialtyDesc}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Category: {i.categoryDesc}
                    </div>
                  </div>
                );
              }
              return null;
            }}
            onLoadMore={(section, searchQuery) => {
              dispatch(
                setViewMoreDialog({
                  isVisible: true,
                  searchBy: section.title,
                  viewMoreSearchCriteria: searchQuery,
                }),
              );
            }}
            onSelect={(item) => onFreeTextResultSelect(item)}
          />
        </section>
        {/* Search by Provider Category */}
        <section>
          <h2 className="mb-2 text-sm font-medium text-gray-700">
            Search by Provider Category
          </h2>
          <AnimatePresence initial={false}>
            <motion.div
              initial={false}
              animate={{ height: expanded ? "auto" : 240 }}
              exit={{ height: 0 }}
              transition={{ duration: 0.4, ease: "easeInOut" }}
              className="overflow-hidden"
            >
              <div className="flex flex-wrap gap-4">
                {renderSearchByCategorySection()}
              </div>
            </motion.div>
          </AnimatePresence>
          <div className="mt-4 flex justify-center">
            <button
              className="flex items-center text-sm text-primary hover:underline"
              onClick={toggle}
            >
              {expanded ? "View fewer categories" : "View all categories"}
              <span className="ml-1">{expanded ? "▲" : "▼"}</span>
            </button>
          </div>
        </section>
        {/* Filter Section */}
        <section>
          <div className="grid gap-4 sm:grid-cols-3">
            {/* Provider Specialty Dropdown */}
            <div className="space-y-2">
              <Label>Select Provider Specialty</Label>
              <Dropdown<ProviderSearchSpecialtyType>
                data={category?.specialtyList ?? []}
                value={specialty}
                placeholder="Select a Specialty"
                multiple={false}
                renderItem={(item) => <span>{item.specialtyDesc}</span>}
                getItemValue={(item) => specialty && item.specialtyDesc}
                onChange={(selectedSpecialty) => {
                  if (selectedSpecialty) {
                    dispatch(
                      setSpecialty(
                        selectedSpecialty as ProviderSearchSpecialtyType,
                      ),
                    );
                  }
                }}
              />
            </div>
            {/* Location  */}
            <div className="space-y-2">
              <SearchAutocomplete<Place, { queryString: string }>
                label="Location"
                placeholder="Enter a location"
                placeholderIcon={MapPin}
                value={location?.description}
                queryFunction={locationSearchQuery}
                onSelect={async (value: Place) => {
                  dispatch(setLocation(value));
                }}
                formatResult={(result: Place, _) => {
                  return (
                    <div key={result.place_id}>
                      <div className="text-sm font-medium">
                        {result.description}
                      </div>
                      {/* <div className="text-sm font-medium">
                        {`Lat: ${result.lat.toFixed(4)}, Lng: ${result.lng.toFixed(4)}`}
                      </div> */}
                    </div>
                  );
                }}
                onClear={() => {
                  dispatch(setLocation(null));
                }}
                minSearchLength={2}
                debounceTime={200}
              />
            </div>
            {/* Distance */}
            <div className="space-y-2">
              <Label>Distance</Label>
              <Dropdown<Distance>
                data={DISTANCE_OPTIONS}
                value={distance}
                placeholder="Select Distance"
                multiple={false}
                renderItem={(item) => <span>{item.displayName}</span>}
                getItemValue={(item) => distance && item?.displayName}
                onChange={(selectedDistance) => {
                  if (selectedDistance) {
                    dispatch(setDistance(selectedDistance as Distance));
                  }
                }}
              />
            </div>
          </div>
        </section>
        {/* Search Button */}
        <section>
          <Button
            className="w-full"
            type="button"
            variant="default"
            onClick={onSearch}
          >
            Search
          </Button>
        </section>
      </div>
      <SpecialityListDialog />
      {viewMoreDialog?.isVisible ? (
        <ProviderSearchFreeTextViewMore onSelect={onFreeTextResultSelect} />
      ) : null}
      <ValidationDialog
        dialogOpen={openValidationDialog.isOpen}
        dialogContent={openValidationDialog.content}
        setDialogOpen={setOpenValidationDialog}
      />
    </PageContainer>
  );
};

export default ProviderSearch;
